import React, { useState, useEffect, useMemo } from 'react';
import { UserViewPreferences } from '@amzn/controllershiphub-typescript-client';
import { FilterModel } from 'ag-grid-community';
import { AppliedFilters } from '../AppliedFilters';
import { getUserPreferences, saveUserPreferences } from 'src/utils/ControlSummaryUtils';
import { DELAY } from 'src/constants';
import {
  processAgGridFilters,
  extractSelectedValues,
  convertToFilterPreference,
  formatDisplayNames,
} from 'src/components/Filter/Utils/FilterUtils';
import { FilterViewManagerProps, DEFAULT_VIEW } from './types';
import { useControlMetadata } from 'src/api/hooks/useControlMetadata';
import { Flashbar } from '@cloudscape-design/components';
import { useFlashbar } from 'src/common/hooks/useFlashbar';
import { shareUserView } from 'src/utils/ShareViewUtils';

export const UserViewActions: React.FC<FilterViewManagerProps> = ({
  controlId,
  filters,
  agGridFilters,
  displayNameMapping,
  onViewSelect,
  gridRef,
  onLoadMore,
  hasMoreData,
  isLoadingMore,
  onExportData,
  onPivotExportData,
  dataType,
  setIsPivotModalVisible,
}) => {
  const [filterViews, setFilterViews] = useState<Record<string, UserViewPreferences>>({
    [DEFAULT_VIEW]: {
      filterPreferences: {},
      columnPreferences: '',
    },
  });
  const [selectedView, setSelectedView] = useState<string>(DEFAULT_VIEW);
  const [isLoadingViews, setIsLoadingViews] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const { flashbarMessages, addFlashbarMessage, clearFlashbarMessages } = useFlashbar();
  const { formattedFilters, defaultColumnState } = useControlMetadata(controlId);

  // Debug: Log what we get from the API
  useEffect(() => {
    console.log('Default column state from API:', defaultColumnState);
  }, [defaultColumnState]);

  // Load saved views
  useEffect(() => {
    const loadViews = async () => {
      try {
        const preferences = await getUserPreferences(controlId);
        setFilterViews((views) => ({
          [DEFAULT_VIEW]: views[DEFAULT_VIEW],
          ...(preferences?.views || {}),
        }));
      } catch (error) {
        console.error('Error loading views:', error);
      }
    };
    loadViews();
  }, [controlId]);

  // Clear the Error Message
  const clearShareError = () => {
    setShareError(null);
  };

  // Filter out Default View from savedFilterViews when it's selected
  const visibleFilterViews = useMemo(() => {
    if (selectedView === DEFAULT_VIEW) {
      const { [DEFAULT_VIEW]: _, ...restViews } = filterViews;
      return restViews;
    }
    return filterViews;
  }, [filterViews, selectedView]);

  const handleFilterViewSelect = async (viewName: string) => {
    try {
      setSelectedView(viewName);

      if (viewName === DEFAULT_VIEW) {
        await onViewSelect(formattedFilters || {}, {});

        // Apply default column state after data loading is complete
        if (defaultColumnState && defaultColumnState.length > 0 && gridRef) {
          const grid = typeof gridRef === 'function' ? null : gridRef.current;
          if (grid && grid.api && grid.api.applyColumnState) {
            // Wait for data loading to complete before applying column state
            // Skip delay in test environment
            setTimeout(() => {
              console.log(`[${viewName}] Applying default column state:`, defaultColumnState);
              grid.api.applyColumnState({
                state: defaultColumnState,
                applyOrder: true,
                defaultState: { hide: false },
              });
              console.log(`[${viewName}] Default column state applied`);
            }, DELAY);
          }
        }
        return;
      }

      const selectedView = filterViews[viewName];
      if (!selectedView?.filterPreferences) return;
      const filterPrefs = selectedView.filterPreferences as FilterModel;

      const panelFilters = extractSelectedValues(filterPrefs);

      await onViewSelect(panelFilters, filterPrefs);

      // Apply column state after data loading is complete
      if (selectedView.columnPreferences && gridRef) {
        try {
          const columnPrefs = JSON.parse(selectedView.columnPreferences);
          console.log(`[${viewName}] Column state to apply:`, columnPrefs.state);
          const grid = typeof gridRef === 'function' ? null : gridRef.current;
          if (grid && grid.api && grid.api.applyColumnState && columnPrefs.state) {
            // Log hidden columns before applying
            const hiddenCols = columnPrefs.state.filter((col: any) => col.hide);
            console.log(
              `[${viewName}] Columns to hide:`,
              hiddenCols.map((col: any) => col.colId)
            );

            // Wait for data loading to complete before applying column state
            // Skip delay in test environment
            setTimeout(() => {
              grid.api.applyColumnState({
                state: columnPrefs.state,
                applyOrder: true,
                defaultState: { hide: false },
              });
              console.log(`[${viewName}] Column state applied with defaultState`);

              // Verify the column state was applied correctly
              setTimeout(() => {
                const currentState = grid.api.getColumnState();
                const actuallyHidden = currentState.filter((col: any) => col.hide);
                console.log(
                  `[${viewName}] Hidden columns after application:`,
                  actuallyHidden.map((col: any) => col.colId)
                );
              }, 100);
            }, DELAY);
          }
        } catch (error) {
          console.error(`[${viewName}] Error applying column preferences:`, error);
        }
      }
    } catch (error) {
      console.error('Error selecting view:', error);
    }
  };

  // Create View Preference based on the Filters selected
  const createViewPreference = (): UserViewPreferences => {
    let columnState = [];
    const grid = typeof gridRef === 'function' ? null : gridRef?.current;
    if (grid && 'getColumnState' in grid) {
      columnState = (grid as any).getColumnState();
    }

    const processedAgGridFilters = processAgGridFilters(agGridFilters);
    const processedPanelFilters = Object.entries(filters)
      .filter(([_, values]) => values && values.length > 0)
      .reduce(
        (acc, [key, values]) => {
          acc[key] = convertToFilterPreference(values);
          return acc;
        },
        {} as Record<string, any>
      );

    return {
      filterPreferences: {
        ...processedPanelFilters,
        ...processedAgGridFilters,
      },
      columnPreferences: JSON.stringify({
        state: columnState,
        version: 1,
      }),
    };
  };

  // Handle Save View
  const handleSaveFilterSet = async (filterSetName: string): Promise<string> => {
    setIsLoadingViews(true);

    try {
      const selectedViewPreference = createViewPreference();
      selectedViewPreference.shared = false;
      const existingPrefs = await getUserPreferences(controlId);
      const updatedViews = {
        ...(existingPrefs?.views || {}),
        [filterSetName]: selectedViewPreference,
      };

      await saveUserPreferences(controlId, updatedViews);
      setFilterViews(updatedViews);

      return filterSetName;
    } finally {
      setIsLoadingViews(false);
    }
  };

  // Handle Share View
  const handleShareView = async (
    viewName: string,
    aliases: string,
    description: string
  ): Promise<void> => {
    setIsSharing(true);
    setShareError(null);

    try {
      const viewPreference = createViewPreference();

      await shareUserView(controlId, viewName, aliases, description, viewPreference);

      addFlashbarMessage({
        type: 'success',
        content: `View shared successfully`,
      });
    } catch (error) {
      console.error('Error sharing view:', error);
      setShareError(`Failed to Share View: ${error}`);
      throw error;
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <>
      {notifications.length > 0 && <Flashbar items={notifications} />}
      <AppliedFilters
        activeFilters={{
          ...(filters ? formatDisplayNames(filters, displayNameMapping) : {}),
          ...formatDisplayNames(extractSelectedValues(agGridFilters), displayNameMapping),
        }}
        onSaveFilterView={handleSaveFilterSet}
        onShareView={handleShareView}
        hasActiveFilters={
          Object.values(filters || {}).some((arr) => arr.length > 0) ||
          Object.keys(agGridFilters).length > 0
        }
        savedFilterViews={visibleFilterViews}
        onFilterViewSelect={handleFilterViewSelect}
        currentViewName={selectedView}
        loading={isLoadingViews}
        isSharing={isSharing}
        shareError={shareError}
        clearShareError={clearShareError}
        onLoadMore={onLoadMore}
        hasMoreData={hasMoreData}
        isLoadingMore={isLoadingMore}
        onExportData={onExportData}
        onPivotExportData={onPivotExportData}
        controlId={controlId}
        dataType={dataType}
        setIsPivotModalVisible={setIsPivotModalVisible}
      />
    </>
  );
};
